import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { confirmAlert } from "react-confirm-alert";
import { findByComunicado, save, remove } from "./api-comunicado-turma";
import { countAlunos, countResponsaveis } from "../../AlunoTurma/api-aluno-turma";
import TurmaSelectDialog from "../../Turma/TurmaSelectDialog";
import { errorMessage } from "../../../util/ConfirmAlertUtil";
import { errorToast } from "../../../util/ToastUtil";
import Loading from "../../UI/Loading";

const toNumber = (value) => (typeof value === "number" ? value : 0);

const Total = ({ label, value }) => (
  <Box flex={1} textAlign="center">
    <Typography variant="h6">{value}</Typography>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Box>
);

export default function ComunicadoTurmaTable({ comunicado }) {
  const [comunicadoTurmas, setComunicadoTurmas] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [openSearchTurmas, setOpenSearchTurmas] = useState(false);
  // Quantidade de alunos/responsáveis por código de turma.
  const [counts, setCounts] = useState({});

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    findByComunicado(comunicado.codigo)
      .then((data) => {
        if (!active || !data) return;
        if (data.error) {
          errorMessage(data.message);
        } else {
          setComunicadoTurmas(data);
        }
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [comunicado.codigo]);

  useEffect(() => {
    let active = true;
    Promise.all(
      comunicadoTurmas.map((ct) =>
        Promise.all([countAlunos(ct.turma.codigo, true), countResponsaveis(ct.turma.codigo, true)]).then(
          ([alunos, responsaveis]) => [ct.turma.codigo, { alunos: toNumber(alunos), responsaveis: toNumber(responsaveis) }],
        ),
      ),
    ).then((entries) => {
      if (active) setCounts(Object.fromEntries(entries));
    });
    return () => {
      active = false;
    };
  }, [comunicadoTurmas]);

  const addTurmas = (turmas) => {
    Promise.all(
      turmas.map((turma) => save({ comunicado, turma }).then((data) => ({ turma, data }))),
    ).then((results) => {
      const salvas = results.filter(({ data }) => data && !data.error).map(({ data }) => data);
      const duplicadas = results.filter(({ data }) => data?.error).map(({ turma }) => turma.descricao);
      if (salvas.length > 0) {
        setComunicadoTurmas((prevState) => [...prevState, ...salvas]);
      }
      if (duplicadas.length > 0) {
        errorToast(`Turma(s) já cadastrada(s): ${duplicadas.join(", ")}.`);
      }
    });
  };

  const handleTurmasSelected = (turmas) => {
    if (!turmas || turmas.length === 0) return;
    confirmAlert({
      title: "Alerta",
      message: `Deseja adicionar ${turmas.length} turma(s) no comunicado?`,
      buttons: [
        { label: "Sim", onClick: () => addTurmas(turmas) },
        { label: "Não", onClick: () => {} },
      ],
    });
  };

  const handleDelete = (comunicadoTurma) => {
    confirmAlert({
      title: "Alerta",
      message: `Deseja remover a turma ${comunicadoTurma.turma.descricao} do comunicado?`,
      buttons: [
        {
          label: "Sim",
          onClick: () => {
            remove(comunicadoTurma.codigo).then((data) => {
              if (data?.error) {
                errorMessage(data.message);
                return;
              }
              setComunicadoTurmas((prevState) =>
                prevState.filter((ct) => ct.codigo !== comunicadoTurma.codigo),
              );
            });
          },
        },
        { label: "Não", onClick: () => {} },
      ],
    });
  };

  const totalAlunos = Object.values(counts).reduce((sum, c) => sum + c.alunos, 0);
  const totalResponsaveis = Object.values(counts).reduce((sum, c) => sum + c.responsaveis, 0);

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2 }}>
      <TurmaSelectDialog
        openSearchTurmas={openSearchTurmas}
        setOpenSearchTurmas={setOpenSearchTurmas}
        setTurmasSelected={handleTurmasSelected}
      />

      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} p={2}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle1" fontWeight={600}>
            Turmas
          </Typography>
          <Chip size="small" label={comunicadoTurmas.length} />
        </Stack>
        {!comunicado.enviado && (
          <Button size="small" startIcon={<AddIcon />} onClick={() => setOpenSearchTurmas(true)}>
            Adicionar
          </Button>
        )}
      </Stack>
      <Divider />

      {isLoading ? (
        <Box py={4}>
          <Loading size={28} description="Carregando Turmas..." />
        </Box>
      ) : comunicadoTurmas.length === 0 ? (
        <Typography variant="body2" color="text.secondary" textAlign="center" py={4} px={2}>
          Nenhuma turma adicionada.
        </Typography>
      ) : (
        <List dense disablePadding sx={{ maxHeight: 360, overflowY: "auto" }}>
          {comunicadoTurmas.map((ct) => {
            const count = counts[ct.turma.codigo];
            return (
              <ListItem
                key={ct.codigo}
                divider
                secondaryAction={
                  !comunicado.enviado && (
                    <Tooltip title="Remover turma">
                      <IconButton edge="end" size="small" onClick={() => handleDelete(ct)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )
                }
              >
                <ListItemText
                  primary={`${ct.turma.curso?.abreviacao ?? ""} ${ct.turma.descricao}`.trim()}
                  secondary={
                    count ? `${count.alunos} alunos · ${count.responsaveis} responsáveis` : " "
                  }
                />
              </ListItem>
            );
          })}
        </List>
      )}

      <Divider />
      <Stack direction="row" divider={<Divider orientation="vertical" flexItem />} py={1.5}>
        <Total label="Alunos" value={totalAlunos} />
        <Total label="Responsáveis" value={totalResponsaveis} />
        <Total label="Total" value={totalAlunos + totalResponsaveis} />
      </Stack>
    </Paper>
  );
}
